import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { ILuminaria, TipoValoresReporte } from 'modelos/src';
import { Subscription } from 'rxjs';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';

@Component({
  selector: 'app-detalles-luminaria',
  templateUrl: './detalles-luminaria.component.html',
  styleUrl: '../../../../../componentes/detalle/detalle-pagina.scss',
  standalone: false,
})
export class DetallesLuminariaComponent implements OnInit, OnDestroy {
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);

  public loading: boolean = true;
  public selectedTab = 0;
  public puedeEditar = this.helper.tienePermiso('Luminarias - Editar');
  public tipoValoresReportePeriodico?: TipoValoresReporte;
  public tipoValoresReporteEnergia?: TipoValoresReporte;
  // Luminaria
  public idLuminaria: string | null = null;
  public luminaria?: ILuminaria;
  private luminaria$: Subscription;
  private params$?: Subscription;
  private queryParams$?: Subscription;

  get esLuminariaConConsumo(): boolean {
    return (
      this.luminaria?.dispositivo?.tipo === 'Luminaria GPE' ||
      this.luminaria?.dispositivo?.tipo === 'Luminaria ACTIS FING'
    );
  }

  /** El domicilio identifica a la luminaria: va de subtítulo. */
  get direccion(): string | undefined {
    const d = this.luminaria?.direccion;
    return d ? HelperService.extraerDireccion(d) || undefined : undefined;
  }

  /**
   * Las pestañas en orden, por nombre: el query param `tab` va por nombre
   * (?tab=eventosLora) porque el índice cambia con la de consumo, que solo
   * tienen GPE y ACTIS. Tiene que coincidir con la plantilla.
   */
  private pestanas(): string[] {
    return [
      'info',
      'perfil',
      'reportes',
      ...(this.esLuminariaConConsumo ? ['consumo'] : []),
      'eventos',
      'comandos',
      'notas',
      'contactos',
      'servicioTecnico',
      'eventosLora',
      'asignaciones',
    ];
  }

  public tabChanged(event: MatTabChangeEvent): void {
    this.selectedTab = event.index;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { tab: this.pestanas()[event.index] ?? event.index },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** `tab` por nombre, o por índice (links viejos). */
  private indicePestana(tab: string): number | null {
    if (/^\d+$/.test(tab)) return parseInt(tab);
    const i = this.pestanas().indexOf(tab);
    return i >= 0 ? i : null;
  }

  private async listarLuminaria() {
    return new Promise<void>((resolve) => {
      this.luminaria$?.unsubscribe();
      // Si la luminaria no existe (se borró, no hay acceso) el getById falla:
      // sin el error la página quedaba con el spinner para siempre.
      this.luminaria$ = this.lista2.luminaria(this.idLuminaria).subscribe({
        next: (data) => {
          this.luminaria = data;
          resolve();
        },
        error: () => resolve(),
      });
    });
  }

  public editar(): void {
    this.router.navigate([
      'main/luminarias/luminarias/editar',
      this.idLuminaria,
    ]);
  }

  /**
   * Carga la luminaria del id de la ruta. Se llama cada vez que cambia el id:
   * Angular reutiliza el componente al ir de una luminaria a otra, y antes la
   * página seguía mostrando la anterior.
   */
  private async cargar(id: string | null): Promise<void> {
    this.loading = true;
    this.luminaria = undefined;
    this.idLuminaria = id;
    await this.listarLuminaria();

    //Define qué tipo de reporte de valores usar según el tipo de luminaria
    this.tipoValoresReporteEnergia = undefined;
    this.tipoValoresReportePeriodico = undefined;
    const tipoLuminaria = this.luminaria?.dispositivo?.tipo;
    if (tipoLuminaria === 'Luminaria GPE') {
      this.tipoValoresReporteEnergia = 'Luminaria GPE Energía';
      this.tipoValoresReportePeriodico = 'Luminaria GPE Periódico';
    }
    if (tipoLuminaria === 'Luminaria ACTIS FING') {
      this.tipoValoresReporteEnergia = 'Luminaria ACTIS FING Energía';
      this.tipoValoresReportePeriodico = 'Luminaria ACTIS FING Estado';
    }
    if (tipoLuminaria === 'Luminaria Wellness') {
      this.tipoValoresReportePeriodico = 'Luminaria Wellness';
    }
    this.loading = false;
  }

  ngOnInit(): void {
    this.params$ = this.activatedRoute.paramMap.subscribe((params) => {
      void this.cargar(params.get('id'));
    });
    this.queryParams$ = this.activatedRoute.queryParams.subscribe((params) => {
      const indice = params['tab'] ? this.indicePestana(params['tab']) : null;
      if (indice !== null) this.selectedTab = indice;
    });
  }

  ngOnDestroy(): void {
    this.luminaria$?.unsubscribe();
    this.params$?.unsubscribe();
    this.queryParams$?.unsubscribe();
  }
}
